import { Component } from '@theme/component';

/**
 * Wraps an autoplaying <video> element and exposes a keyboard-accessible
 * pause/play toggle to comply with WCAG 2.2.2 (Pause, Stop, Hide).
 *
 * Expects a single <video> descendant as the playback target. Looks up the
 * toggle button via the `toggleButton` ref. Reads pause/play aria-labels from
 * the button's data-label-pause / data-label-play attributes.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggleButton
 *
 * @extends Component<Refs>
 */
export class PausableVideoComponent extends Component {
  requiredRefs = ['toggleButton'];

  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Set once the visitor pauses the video themselves, so that scrolling it out
   * of view and back does not restart playback against their wishes.
   * @type {boolean}
   */
  #pausedByUser = false;

  connectedCallback() {
    super.connectedCallback();

    this.#video = this.querySelector('video');
    if (!this.#video) return;

    this.#syncToggleState();
    this.#video.addEventListener('play', () => this.#syncToggleState());
    this.#video.addEventListener('pause', () => this.#syncToggleState());

    this.#observeVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  togglePlayback() {
    if (!this.#video) return;
    if (this.#video.paused) {
      this.#pausedByUser = false;
      this.#video.play();
    } else {
      this.#pausedByUser = true;
      this.#video.pause();
    }
  }

  /**
   * Requests playback whenever the video scrolls into view.
   *
   * Inside a carousel the video starts off-screen, and browsers only apply the
   * autoplay attribute once the element is visible. Some, notably Safari on iOS
   * with Low Power Mode or "Auto-Play Video Previews" off, refuse to start it at
   * all. Asking explicitly covers the first case; when the request is rejected
   * the video simply stays paused and the toggle keeps showing its play icon,
   * which the visitor can tap.
   */
  #observeVisibility() {
    const video = this.#video;
    if (!video?.hasAttribute('autoplay')) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || this.#pausedByUser || !video.paused) continue;
          // Rejection is expected when the browser blocks autoplay; the catch
          // keeps it from surfacing as an unhandled promise rejection.
          video.play().catch(() => this.#syncToggleState());
        }
      },
      { threshold: 0.5 }
    );

    // The <pausable-video> wrapper can be `display: contents`, which has no box
    // of its own to observe, so the video element is the target.
    this.#observer.observe(video);
  }

  #syncToggleState() {
    const { toggleButton } = this.refs;
    if (!toggleButton || !this.#video) return;

    const isPaused = this.#video.paused;
    toggleButton.dataset.state = isPaused ? 'paused' : 'playing';
    const label = isPaused ? toggleButton.dataset.labelPlay : toggleButton.dataset.labelPause;
    if (label) toggleButton.setAttribute('aria-label', label);
  }
}

if (!customElements.get('pausable-video')) {
  customElements.define('pausable-video', PausableVideoComponent);
}
